import type { ViewRData } from "../../types/unity";

const DEFAULT_IMPORT_SELECTION_TIMEOUT_MS = 6000;
const DEFAULT_IMPORT_SELECTION_POLL_INTERVAL_MS = 250;
const DEFAULT_IMPORT_SELECTION_SETTLE_DELAY_MS = 80;

export interface UnityMeasureToggledDetail {
  toggled: boolean;
}

export interface ImportedVseSelectionContext {
  targetSpaceIndex: number;
  resolvedTargetSpaceIndex: number | null;
  baselineVseCount: number;
  uploadItemId?: string;
  requestId?: string;
}

interface SidebarViewRDataDetail {
  viewRData?: ViewRData;
  currentSpaceIndex?: number;
}

interface SendToUnity {
  (eventType: string, payload: Record<string, unknown>): Promise<unknown>;
}

interface WaitForImportedVseSelectionOptions {
  initialViewRData?: ViewRData | null;
  selectionContext: ImportedVseSelectionContext;
  sendToUnity: SendToUnity;
  timeoutMs?: number;
  pollIntervalMs?: number;
  settleDelayMs?: number;
  /** false이면 현재 Space를 강제로 바꾸지 않고 caller가 복구 CTA로 전환한다. */
  allowTargetSpaceSwitch?: boolean;
  requireSelectionConfirmation?: boolean;
  assertCurrent?: () => void;
  signal?: AbortSignal;
  log?: (message: string, data?: Record<string, unknown>) => void;
}

export interface ImportedVseSelectionResult {
  spaceIndex: number;
  vseIndex: number;
}

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const abort = () => {
      window.clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
      reject(new DOMException("Import execution ended", "AbortError"));
    };
    const timer = window.setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", abort);
    if (signal?.aborted) abort();
  });

const readBoolean = (value: unknown): boolean | null => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return null;
};

const parseJsonString = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const normalizeUnityMeasureToggledDetail = (
  detail: unknown,
): UnityMeasureToggledDetail | null => {
  const direct = readBoolean(detail);
  if (direct !== null) {
    return { toggled: direct };
  }

  if (typeof detail === "string") {
    return normalizeUnityMeasureToggledDetail(parseJsonString(detail));
  }

  if (!detail || typeof detail !== "object") {
    return null;
  }

  const record = detail as Record<string, unknown>;
  const candidates = [
    record.toggled,
    record.measureToggled,
    record.enabled,
    record.isOn,
    record.active,
  ];

  for (const candidate of candidates) {
    const parsed = readBoolean(candidate);
    if (parsed !== null) {
      return { toggled: parsed };
    }
  }

  return null;
};

export const getVseCountInSpace = (
  viewRData: ViewRData | null | undefined,
  spaceIndex: number | null | undefined,
): number => {
  if (!viewRData?.spaces?.length || typeof spaceIndex !== "number") {
    return 0;
  }

  const safeSpaceIndex = Math.min(
    Math.max(Math.trunc(spaceIndex), 0),
    viewRData.spaces.length - 1,
  );

  return viewRData.spaces[safeSpaceIndex]?.vseList?.length ?? 0;
};

export const resolveImportTargetSpaceIndex = (
  viewRData: ViewRData | null | undefined,
  targetSpaceIndex: number | null | undefined,
): number | null => {
  if (
    typeof targetSpaceIndex === "number" &&
    Number.isFinite(targetSpaceIndex) &&
    targetSpaceIndex >= 0
  ) {
    return Math.trunc(targetSpaceIndex);
  }

  if (!viewRData?.spaces?.length) {
    return null;
  }

  return Math.min(
    Math.max(Math.trunc(viewRData.currentSelectVSIndex ?? 0), 0),
    viewRData.spaces.length - 1,
  );
};

export const createImportedVseSelectionContext = ({
  viewRData,
  targetSpaceIndex,
  uploadItemId,
  requestId,
  baselineVseCount,
}: {
  viewRData?: ViewRData | null;
  targetSpaceIndex: number;
  uploadItemId?: string;
  requestId?: string;
  /** attach 후 슬롯이 이미 반영된 경우(예: library attach) import 직전 VSE 개수 */
  baselineVseCount?: number;
}): ImportedVseSelectionContext => {
  const resolvedTargetSpaceIndex = resolveImportTargetSpaceIndex(
    viewRData,
    targetSpaceIndex,
  );

  const resolvedBaseline =
    typeof baselineVseCount === "number" && Number.isFinite(baselineVseCount)
      ? Math.max(0, Math.trunc(baselineVseCount))
      : getVseCountInSpace(viewRData, resolvedTargetSpaceIndex);

  return {
    targetSpaceIndex,
    resolvedTargetSpaceIndex,
    baselineVseCount: resolvedBaseline,
    uploadItemId,
    requestId,
  };
};

const buildSpaceSelectionPayload = (
  viewRData: ViewRData | null | undefined,
  targetSpaceIndex: number,
):
  | {
      space: {
        id: string;
        name: string;
        index: number;
      };
      spaces: Array<{
        id: string;
        name: string;
        index: number;
      }>;
    }
  | null => {
  if (!viewRData?.spaces?.length) return null;

  const safeSpaceIndex = Math.min(
    Math.max(Math.trunc(targetSpaceIndex), 0),
    viewRData.spaces.length - 1,
  );
  const spaces = viewRData.spaces.map((space, index) => ({
    id: `space-import-${index}`,
    name: space?.name || `Space${index + 1}`,
    index,
  }));
  const currentSpace = spaces[safeSpaceIndex];

  if (!currentSpace) return null;

  return {
    space: currentSpace,
    spaces,
  };
};

const resolveImportedVseSelection = (
  viewRData: ViewRData | null | undefined,
  selectionContext: ImportedVseSelectionContext,
): ImportedVseSelectionResult | null => {
  const targetSpaceIndex = selectionContext.resolvedTargetSpaceIndex;
  if (typeof targetSpaceIndex !== "number") {
    return null;
  }

  const currentCount = getVseCountInSpace(viewRData, targetSpaceIndex);
  if (currentCount <= selectionContext.baselineVseCount) {
    return null;
  }

  const vseIndex = selectionContext.baselineVseCount;
  if (vseIndex < 0 || vseIndex >= currentCount) {
    return null;
  }

  return {
    spaceIndex: targetSpaceIndex,
    vseIndex,
  };
};

export const waitForImportedVseSelection = async ({
  initialViewRData = null,
  selectionContext,
  sendToUnity,
  timeoutMs = DEFAULT_IMPORT_SELECTION_TIMEOUT_MS,
  pollIntervalMs = DEFAULT_IMPORT_SELECTION_POLL_INTERVAL_MS,
  settleDelayMs = DEFAULT_IMPORT_SELECTION_SETTLE_DELAY_MS,
  allowTargetSpaceSwitch = true,
  requireSelectionConfirmation = false,
  assertCurrent,
  signal,
  log,
}: WaitForImportedVseSelectionOptions): Promise<ImportedVseSelectionResult | null> => {
  let latestViewRData = initialViewRData;
  let currentSpaceIndex =
    typeof initialViewRData?.currentSelectVSIndex === "number"
      ? initialViewRData.currentSelectVSIndex
      : null;
  let lastViewRDataRequestAt = 0;
  let lastSelectSpaceRequestAt = 0;
  const deadline = Date.now() + timeoutMs;

  const handleViewRData = (event: Event) => {
    const detail = (event as CustomEvent<SidebarViewRDataDetail>).detail;
    latestViewRData = detail?.viewRData ?? null;

    if (typeof detail?.currentSpaceIndex === "number") {
      currentSpaceIndex = detail.currentSpaceIndex;
      return;
    }

    if (typeof latestViewRData?.currentSelectVSIndex === "number") {
      currentSpaceIndex = latestViewRData.currentSelectVSIndex;
    }
  };

  const handleSpaceSelected = (event: Event) => {
    const detail = (
      event as CustomEvent<{ space?: { index?: number } } | undefined>
    ).detail;
    if (typeof detail?.space?.index === "number") {
      currentSpaceIndex = detail.space.index;
    }
  };

  window.addEventListener("sidebar:setViewRData", handleViewRData as EventListener);
  window.addEventListener("unity:spaceSelected", handleSpaceSelected as EventListener);

  try {
    while (Date.now() < deadline) {
      if (signal?.aborted) throw new DOMException("Import execution ended", "AbortError");
      assertCurrent?.();
      const importedSelection = resolveImportedVseSelection(
        latestViewRData,
        selectionContext,
      );

      if (importedSelection) {
        if (currentSpaceIndex !== importedSelection.spaceIndex) {
          if (!allowTargetSpaceSwitch) {
            log?.("imported vse selection requires target space restore", {
              requestId: selectionContext.requestId,
              uploadItemId: selectionContext.uploadItemId,
              targetSpaceIndex: importedSelection.spaceIndex,
              currentSpaceIndex,
            });
            return null;
          }
          const spacePayload = buildSpaceSelectionPayload(
            latestViewRData,
            importedSelection.spaceIndex,
          );

          if (
            spacePayload &&
            Date.now() - lastSelectSpaceRequestAt >= pollIntervalMs
          ) {
            lastSelectSpaceRequestAt = Date.now();
            await sendToUnity("reactToUnity_command", {
              command: "tool_selectSpace",
              ...spacePayload,
            }).catch(() => {
              /* ignore */
            });
            log?.("imported vse selectSpace requested", {
              requestId: selectionContext.requestId,
              uploadItemId: selectionContext.uploadItemId,
              targetSpaceIndex: importedSelection.spaceIndex,
            });
          }

          await sleep(settleDelayMs, signal);
          continue;
        }

        if (requireSelectionConfirmation) {
          let dispose = () => {};
          const confirmed = new Promise<void>((resolve, reject) => {
            const abort = () => { dispose(); reject(new DOMException("Import execution ended", "AbortError")); };
            let lastVseSelectedDetail: unknown = null;
            const receive = (event: Event) => {
              let detail = (event as CustomEvent).detail;
              if (typeof detail === "string") { try { detail = JSON.parse(detail); } catch { return; } }
              lastVseSelectedDetail = detail;
              if (detail?.spaceIndex !== importedSelection.spaceIndex || detail?.vseIndex !== importedSelection.vseIndex) return;
              try { assertCurrent?.(); dispose(); resolve(); } catch (error) { dispose(); reject(error); }
            };
            const timer = window.setTimeout(() => {
              dispose();
              log?.("imported vse selection confirmation timed out", {
                requestId: selectionContext.requestId,
                uploadItemId: selectionContext.uploadItemId,
                expected: { spaceIndex: importedSelection.spaceIndex, vseIndex: importedSelection.vseIndex },
                lastVseSelectedDetail,
                timeoutMs,
              });
              reject(new Error("editor_import_selection_unconfirmed"));
            }, timeoutMs);
            window.addEventListener("unity:vseSelected", receive);
            dispose = () => { window.clearTimeout(timer); window.removeEventListener("unity:vseSelected", receive); signal?.removeEventListener("abort", abort); };
            signal?.addEventListener("abort", abort);
            if (signal?.aborted) abort();
          });
          try {
            await Promise.all([confirmed, Promise.resolve().then(() => {
              if (signal?.aborted) throw new DOMException("Import execution ended", "AbortError");
              assertCurrent?.();
              return sendToUnity("reactToUnity_command", { command: "tool_selectVSE", vseIndex: importedSelection.vseIndex });
            })]);
            assertCurrent?.();
          } finally { dispose(); }
        } else {
          await sendToUnity("reactToUnity_command", { command: "tool_selectVSE", vseIndex: importedSelection.vseIndex }).catch(() => { /* legacy compatibility */ });
        }

        log?.("imported vse selected", {
          requestId: selectionContext.requestId,
          uploadItemId: selectionContext.uploadItemId,
          targetSpaceIndex: importedSelection.spaceIndex,
          vseIndex: importedSelection.vseIndex,
        });

        return importedSelection;
      }

      if (Date.now() - lastViewRDataRequestAt >= pollIntervalMs) {
        lastViewRDataRequestAt = Date.now();
        await sendToUnity("reactToUnity_requestViewRData", {}).catch(() => {
          /* ignore */
        });
      }

      await sleep(settleDelayMs, signal);
    }
  } finally {
    window.removeEventListener(
      "sidebar:setViewRData",
      handleViewRData as EventListener,
    );
    window.removeEventListener(
      "unity:spaceSelected",
      handleSpaceSelected as EventListener,
    );
  }

  log?.("imported vse selection timed out", {
    requestId: selectionContext.requestId,
    uploadItemId: selectionContext.uploadItemId,
    targetSpaceIndex: selectionContext.resolvedTargetSpaceIndex,
    baselineVseCount: selectionContext.baselineVseCount,
  });

  return null;
};
